import React, { useState, useEffect, useMemo, forwardRef } from 'react';
import { useLocation } from 'umi';
import GameSelectAll from '@sg/components/GameSelectAll';
import AccessRestricted from '@sg/components/AccessRestricted';
import { getInDevProjectList } from '@sg/services';
import {
  createBtnPoint,
  SGBuriedBtnPoint,
  InDevProjectsTab,
} from '@/pages/strategic/utils/buriedPoint';
import InDevProjectsAll from './pages/InDevProjectsAll';
import InDevProjectsInfo from './pages/InDevProjectsInfo';
import './index.scss';

interface IInDevProjectsProps {
  /** 月份选择（"YYYY-MM" 字符串），由父级 StudioManagement 控制 */
  selectedMonth?: string | null;
  /** 后端可用最大数据时间，透传给 InDevProjectsInfo */
  maxDataTime?: string;
  /** 子页编辑态变化回调（GR / Lifecycle / Notes / Headcount） */
  onEditingChange?: (isEditing: boolean) => void;
  /** 选中项目变化回调，父级用于联动 RoutePrompt 等 */
  onProjectChange?: (projectId: string | null) => void;
}

interface IStudio {
  name: string;
  unified_id: string;
  studio_id?: string;
  originalData?: any;
}

const ALL_STUDIO: IStudio = {
  name: 'All',
  unified_id: 'all',
};

/**
 * InDevProjects 容器组件。
 * 参考 LiveTitle 的组织方式：在容器内统一处理项目选择 + URL 同步，
 * 当选择 "All" 时渲染 InDevProjectsAll（聚合视图），
 * 选中具体项目时渲染 InDevProjectsInfo（详情视图）。
 */
const InDevProjects = forwardRef<any, IInDevProjectsProps>((props, ref) => {
  const { selectedMonth, maxDataTime, onEditingChange, onProjectChange } = props;
  const location = useLocation();

  const [hasProjectAccess, setHasProjectAccess] = useState<boolean>(true);
  const [projectList, setProjectList] = useState<any[]>([]);
  const [studio, setStudio] = useState<IStudio>(ALL_STUDIO);

  // URL 上携带的初始 project_id，用于 GameSelectAll 默认选中态
  const urlProjectId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('project_id') || undefined;
  }, [location.search]);

  // URL 与 projectList 联动同步当前 studio
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const currentSubtitle = searchParams.get('subtitle');
    if (currentSubtitle !== 'projects') {
      return;
    }

    const projectIdFromUrl = searchParams.get('project_id');

    if (!projectIdFromUrl || projectIdFromUrl === 'all') {
      setStudio(ALL_STUDIO);
      return;
    }

    if (projectList.length > 0) {
      const projectFromUrl = projectList.find(
        (p: any) => p.unified_id === projectIdFromUrl,
      );
      if (projectFromUrl) {
        setStudio({
          name: projectFromUrl.name,
          unified_id: projectFromUrl.unified_id,
          studio_id: projectFromUrl.studio_id,
          originalData: projectFromUrl.originalData || projectFromUrl,
        });
      }
    }
  }, [location.search, projectList]);

  // 通知父级选中项目变化（与原 InDevProjects 行为保持一致）
  useEffect(() => {
    if (!onProjectChange) {
      return;
    }
    if (studio.unified_id && studio.unified_id !== 'all') {
      onProjectChange(studio.unified_id);
      return;
    }
    // During remount, studio may briefly be "all" before URL->state sync finishes.
    // Preserve project-scoped month/filter state by reporting URL project_id first.
    if (urlProjectId && urlProjectId !== 'all') {
      onProjectChange(urlProjectId);
      return;
    }
    // 切回 ALL 时通知父级清空 project 维度上下文
    onProjectChange(null);
  }, [studio.unified_id, onProjectChange, urlProjectId]);

  const fetchProjectData = async () => {
    const res = await getInDevProjectList({});
    return (
      res?.map((item: any) => ({
        unified_id: item.project_id,
        name: item.project,
        cover: item.cover,
        studio_id: item.studio_id,
        originalData: item,
      })) || []
    );
  };

  const handleProjectChange = (next: any) => {
    const projectName = next?.name || 'Unknown';
    const projectId = next?.unified_id || '';
    const btnPoint = createBtnPoint(
      InDevProjectsTab,
      'projectSelect',
      `Project Select - Project: ${projectName}${projectId ? ` (ID: ${projectId})` : ''}`,
    );
    if (btnPoint) {
      SGBuriedBtnPoint(btnPoint);
    }
    // URL 由 GameSelectAll 通过 urlParamKey 统一更新；此处不手动 push 路由
  };

  const handleProjectDataLoaded = (data: any[]) => {
    setProjectList(data);
    setHasProjectAccess(data && data.length > 0);
  };

  if (!hasProjectAccess) {
    return <AccessRestricted />;
  }

  return (
    <div className="dbs-home-studio-card-InDevProjects">
      <GameSelectAll
        fetchData={fetchProjectData}
        onChange={handleProjectChange}
        onDataLoaded={handleProjectDataLoaded}
        defaultUnifiedId={urlProjectId}
        urlParamKey="project_id"
        urlBasePath="/strategic/home"
        urlUpdateCondition={(loc) => {
          const params = new URLSearchParams(loc.search);
          return params.get('subtitle') === 'projects';
        }}
      />
      {studio?.unified_id === 'all' ? (
        <InDevProjectsAll studio={studio} studioList={projectList} />
      ) : (
        <InDevProjectsInfo
          ref={ref}
          studio={studio}
          selectedMonth={selectedMonth}
          maxDataTime={maxDataTime}
          onEditingChange={onEditingChange}
          onProjectChange={onProjectChange}
        />
      )}
    </div>
  );
});

InDevProjects.displayName = 'InDevProjects';

export default InDevProjects;
